import type {
  IntellioChartPanel,
  IntellioVisualization3DSpaceModel,
} from "@triedge/ai-assistant-ui";
import { coerceChartPanels } from "./assistantChartArtifactState";
import type { AdmissionsAssistantArtifactWorkspaceRef } from "./types";

export type AssistantVisualization3DArtifactResolvedState = {
  visualization3DArtifact: AdmissionsAssistantArtifactWorkspaceRef;
  visualization3DSpace: IntellioVisualization3DSpaceModel;
  visualization3DPanels: IntellioChartPanel[];
};

export type AssistantVisualization3DArtifactStateResolver = (
  visualization3DArtifact: AdmissionsAssistantArtifactWorkspaceRef,
) => Promise<AssistantVisualization3DArtifactResolvedState | null>;

type AssistantVisualization3DArtifactStateCacheEntry = {
  inFlight?: Promise<AssistantVisualization3DArtifactResolvedState>;
  resolvedState?: AssistantVisualization3DArtifactResolvedState;
};

const assistantVisualization3DArtifactStateCache = new Map<
  string,
  AssistantVisualization3DArtifactStateCacheEntry
>();

const toRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

export const coerceVisualization3DSpace = (
  value: unknown,
): IntellioVisualization3DSpaceModel | null => {
  const record = toRecord(value);
  if (
    !record ||
    typeof record.id !== "string" ||
    typeof record.title !== "string"
  ) {
    return null;
  }
  if (
    !Array.isArray(record.nodes) ||
    !Array.isArray(record.clusters) ||
    !Array.isArray(record.edges)
  ) {
    return null;
  }
  if (record.nodes.length === 0 || record.clusters.length === 0) {
    return null;
  }
  return record as unknown as IntellioVisualization3DSpaceModel;
};

const sleep = (ms: number) =>
  new Promise((resolve) => {
    globalThis.setTimeout(resolve, ms);
  });

const resolveVisualization3DArtifactStateCacheKey = (
  artifact: Pick<AdmissionsAssistantArtifactWorkspaceRef, "sourceRef">,
): string | null => {
  const sourceRef = String(artifact.sourceRef ?? "").trim();
  return sourceRef.length > 0 ? sourceRef : null;
};

export const hydrateVisualization3DArtifactFromDownload = async (
  downloadUrl: string,
): Promise<Pick<
  AssistantVisualization3DArtifactResolvedState,
  "visualization3DSpace" | "visualization3DPanels"
> | null> => {
  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      const response = await fetch(downloadUrl);
      if (response.ok) {
        const payload = await response.json();
        const record = toRecord(payload);
        const space = coerceVisualization3DSpace(record?.space);
        if (space) {
          return {
            visualization3DSpace: space,
            visualization3DPanels: coerceChartPanels(record?.panels),
          };
        }
      }
    } catch {
      // Retry transient visualization artifact hydration failures.
    }
    if (attempt < 2) {
      await sleep(750 * (attempt + 1));
    }
  }
  return null;
};

export const clearAssistantVisualization3DArtifactStateCache = (): void => {
  assistantVisualization3DArtifactStateCache.clear();
};

export const clearAssistantVisualization3DArtifactStateCacheEntry = (
  artifact:
    | Pick<AdmissionsAssistantArtifactWorkspaceRef, "sourceRef">
    | string
    | null
    | undefined,
): void => {
  const cacheKey =
    typeof artifact === "string"
      ? artifact.trim() || null
      : resolveVisualization3DArtifactStateCacheKey(
          artifact ?? { sourceRef: null },
        );
  if (!cacheKey) {
    return;
  }
  assistantVisualization3DArtifactStateCache.delete(cacheKey);
};

const resolveAssistantVisualization3DArtifactStateUncached = async (input: {
  visualization3DArtifact: AdmissionsAssistantArtifactWorkspaceRef;
  initialSpace?: IntellioVisualization3DSpaceModel | null;
  initialPanels?: IntellioChartPanel[];
  refreshVisualization3DArtifactState?: AssistantVisualization3DArtifactStateResolver;
}): Promise<AssistantVisualization3DArtifactResolvedState> => {
  let resolvedArtifact = input.visualization3DArtifact;
  let visualization3DSpace = input.initialSpace ?? null;
  let visualization3DPanels = input.initialPanels ?? [];

  if (
    !visualization3DSpace &&
    input.visualization3DArtifact.sourceRef &&
    input.refreshVisualization3DArtifactState
  ) {
    try {
      const refreshed = await input.refreshVisualization3DArtifactState(
        input.visualization3DArtifact,
      );
      if (refreshed?.visualization3DArtifact) {
        resolvedArtifact = refreshed.visualization3DArtifact;
      }
      if (refreshed?.visualization3DSpace) {
        visualization3DSpace = refreshed.visualization3DSpace;
      }
      if (refreshed?.visualization3DPanels?.length) {
        visualization3DPanels = refreshed.visualization3DPanels;
      }
    } catch {
      // Fall back to signed-url hydration if the refresh contract is unavailable.
    }
  }

  if (!visualization3DSpace) {
    const downloaded = await hydrateVisualization3DArtifactFromDownload(
      resolvedArtifact.downloadUrl,
    );
    if (downloaded?.visualization3DSpace) {
      visualization3DSpace = downloaded.visualization3DSpace;
      visualization3DPanels =
        downloaded.visualization3DPanels.length > 0
          ? downloaded.visualization3DPanels
          : visualization3DPanels;
    }
  }

  if (!visualization3DSpace) {
    throw new Error(
      "Visualization artifact did not include a valid 3D space payload.",
    );
  }

  return {
    visualization3DArtifact: resolvedArtifact,
    visualization3DSpace,
    visualization3DPanels,
  };
};

export const resolveAssistantVisualization3DArtifactState = async (input: {
  visualization3DArtifact: AdmissionsAssistantArtifactWorkspaceRef;
  initialSpace?: IntellioVisualization3DSpaceModel | null;
  initialPanels?: IntellioChartPanel[];
  refreshVisualization3DArtifactState?: AssistantVisualization3DArtifactStateResolver;
  forceRefresh?: boolean;
}): Promise<AssistantVisualization3DArtifactResolvedState> => {
  const cacheKey = resolveVisualization3DArtifactStateCacheKey(
    input.visualization3DArtifact,
  );
  if (!cacheKey) {
    return resolveAssistantVisualization3DArtifactStateUncached(input);
  }

  const existing = assistantVisualization3DArtifactStateCache.get(cacheKey);
  if (!input.forceRefresh && existing?.resolvedState) {
    return existing.resolvedState;
  }
  if (!input.forceRefresh && existing?.inFlight) {
    return existing.inFlight;
  }

  const inFlight = resolveAssistantVisualization3DArtifactStateUncached(input)
    .then((resolvedState) => {
      assistantVisualization3DArtifactStateCache.set(cacheKey, {
        resolvedState,
      });
      return resolvedState;
    })
    .catch((error) => {
      assistantVisualization3DArtifactStateCache.delete(cacheKey);
      throw error;
    });
  assistantVisualization3DArtifactStateCache.set(cacheKey, { inFlight });
  return inFlight;
};
